// A sheet's formulas worked out: the same engine in the browser (what the grid shows) and on the server (what an agent
// reads), so both always agree. A formula is read once into a tree (parse.ts) and never run as code. Formulas are
// worked out in the order their references need (each cell after the cells it reads), so a long chain of running
// totals is as quick as one cell, a loop shows #CYCLE! and never hangs, and a change works out again only the cells
// that read it, at any distance. A formula that gives a block (UNIQUE, SORT, A1:A3*2) spills into the cells beside
// it, as in Google Sheets, unless they hold something (#REF!).

import { MAX_COLS, MAX_ROWS, colName, fromKey, keyOf, parseRect, type Rect } from "./addr";
import { Arr, Budget, RangeV, cmp, isBlock, scalar, toBool, toNum, toStr, type TabView, type Val } from "./core";
import { FUNCS, MISSING, type Args } from "./functions";
import { ParseError, parse, type Ast, type Ref } from "./parse";
import { err, isErr, nowSerial, readInput, type Implied, type Read, type Scalar, SheetErr } from "./values";
import type { NumFmt } from "./format";

export interface BookOpts {
  /** dates typed with the day first (25/10/2026), as the sheet is set */
  dmy?: boolean;
  /** the clock, for TODAY and NOW, and the user's time zone */
  now?: number;
  tz?: string | null;
  /** a cell's chosen number format, so a sum of currency shows currency */
  fmtAt?: (tabId: string, r: number, c: number) => { nf?: NumFmt; dp?: number; sym?: string } | null;
}

interface Dep {
  ti: number;
  r0: number;
  c0: number;
  r1: number;
  c1: number;
}

interface Cell {
  raw: string;
  /** a typed value, read */
  lit?: Read;
  /** a formula, read; null when it could not be */
  ast?: Ast | null;
  perr?: string;
  deps?: Dep[];
  /** reads TODAY or NOW, so it is worked out again with every change */
  vol?: boolean;
  /** reads cells only it knows as it runs (OFFSET, INDIRECT): worked out once more after the others */
  dyn?: boolean;
}

const PARSED = new Map<string, { ast: Ast | null; perr?: string; vol: boolean; dyn?: boolean }>();

function parsed(src: string): { ast: Ast | null; perr?: string; vol: boolean; dyn?: boolean } {
  let p = PARSED.get(src);
  if (!p) {
    try {
      const ast = parse(src);
      // worked out again with every change: the clock, chance, and references made as it runs
      p = { ast, vol: /\b(TODAY|NOW|RAND|RANDBETWEEN|OFFSET|INDIRECT)\s*\(/i.test(src), dyn: /\b(OFFSET|INDIRECT)\s*\(/i.test(src) };
    } catch (e) {
      p = { ast: null, perr: e instanceof ParseError ? e.message : "The formula cannot be read.", vol: false };
    }
    if (PARSED.size > 20_000) PARSED.clear();
    PARSED.set(src, p);
  }
  return p;
}

/** Whether what a person typed is a formula. */
export function isFormula(raw: string): boolean {
  return raw.length > 1 && raw[0] === "=";
}

class Tab implements TabView {
  cells = new Map<number, Cell>();
  formulas = new Set<number>();
  vals = new Map<number, Scalar>();
  impl = new Map<number, Implied>();
  /** the address a formula's HYPERLINK gave its cell */
  links = new Map<number, string>();
  spill = new Map<number, Scalar>();
  spillOf = new Map<number, Rect>();
  /** formulas whose block could not spill, by where it would have gone: a cell freed there works them out again */
  blocked = new Map<number, Rect>();
  rows = 0;
  cols = 0;
  private stale = false;
  constructor(
    public id: string,
    public name: string,
    public ti: number,
  ) {}
  get(r: number, c: number): Scalar {
    const k = keyOf(r, c);
    const cell = this.cells.get(k);
    if (cell) return cell.lit ? cell.lit.v : (this.vals.get(k) ?? null);
    return this.spill.get(k) ?? null;
  }
  grow(r: number, c: number) {
    if (r + 1 > this.rows) this.rows = r + 1;
    if (c + 1 > this.cols) this.cols = c + 1;
  }
  shrinkLater() {
    this.stale = true;
  }
  fit() {
    if (!this.stale) return;
    this.stale = false;
    let rows = 0;
    let cols = 0;
    for (const k of [...this.cells.keys(), ...this.spill.keys()]) {
      const { r, c } = fromKey(k);
      if (r + 1 > rows) rows = r + 1;
      if (c + 1 > cols) cols = c + 1;
    }
    this.rows = rows;
    this.cols = cols;
  }
}

const gidOf = (ti: number, k: number) => ti * 16_777_216 + k;
const splitGid = (g: number) => ({ ti: Math.floor(g / 16_777_216), k: g % 16_777_216 });

export class Book {
  private tabs: Tab[] = [];
  private byId = new Map<string, Tab>();
  /** named ranges (Fixes 9, names.ts), by the name in capitals: the tab their cells are on and the block, or null once
   * all of it was deleted */
  private names = new Map<string, { name: string; tabId: string; box: Rect | null }>();
  private namesSig = "";
  private dirty = new Set<number>();
  private full = true;
  private budget = new Budget();
  /** the address a HYPERLINK gave while the formula being worked out ran */
  private linked: string | null = null;
  /** the look of the first cell of a block the formula being worked out spilled, from the cell it came from */
  private blockLook: Implied | undefined;
  constructor(public opts: BookOpts = {}) {}

  // ------------------------------------------------------------- what goes in

  /** The tabs, in order, by id and name. A tab gone takes its cells; a name changed reads every formula again. */
  setTabs(list: { id: string; name: string }[]) {
    const same = list.length === this.tabs.length && list.every((t, i) => this.tabs[i].id === t.id && this.tabs[i].name === t.name);
    if (same) return;
    const old = this.byId;
    this.tabs = list.map((t, i) => {
      const was = old.get(t.id);
      if (was) {
        was.name = t.name;
        was.ti = i;
        return was;
      }
      return new Tab(t.id, t.name, i);
    });
    this.byId = new Map(this.tabs.map((t) => [t.id, t]));
    for (const t of this.tabs) for (const k of t.formulas) this.readDeps(t, k);
    this.full = true;
  }

  /** The sheet's named ranges: a change reads every formula's references again and works them all out. */
  setNames(list: { name: string; tabId: string; range: string }[]) {
    const sig = JSON.stringify(list.map((n) => [n.name.toUpperCase(), n.tabId, n.range]));
    if (sig === this.namesSig) return;
    this.namesSig = sig;
    this.names = new Map(list.map((n) => [n.name.toUpperCase(), { name: n.name, tabId: n.tabId, box: n.range === "#REF!" ? null : parseRect(n.range) }]));
    for (const t of this.tabs) for (const k of t.formulas) this.readDeps(t, k);
    this.full = true;
  }

  /** The block a name stands for, as the engine reads it: its tab and cells, null when it was deleted, undefined for
   * a word that names no range. */
  named(word: string): { tab: string; name: string; box: Rect | null } | undefined {
    const n = this.names.get(word.toUpperCase());
    return n ? { tab: n.tabId, name: n.name, box: n.box } : undefined;
  }

  /** What a person typed in a cell, or null to empty it. */
  setCell(tabId: string, r: number, c: number, raw: string | null) {
    const t = this.byId.get(tabId);
    if (!t || r < 0 || c < 0 || r >= MAX_ROWS || c >= MAX_COLS) return;
    const k = keyOf(r, c);
    const was = t.cells.get(k);
    if ((was?.raw ?? null) === (raw || null)) return;
    // a formula's spilled block goes with it; a cell typed into a block, or freed in one that could not spill, works
    // out the formula that spills there again
    const spilled = t.spillOf.get(k);
    if (spilled) {
      for (let i = spilled.r0; i <= spilled.r1; i++)
        for (let j = spilled.c0; j <= spilled.c1; j++) {
          const sk = keyOf(i, j);
          if (sk !== k && t.spill.delete(sk)) {
            this.dirty.add(gidOf(t.ti, sk));
            if (!t.cells.has(sk)) t.impl.delete(sk);
          }
        }
      t.spillOf.delete(k);
    }
    t.blocked.delete(k);
    for (const [src, box] of [...t.spillOf, ...t.blocked]) if (src !== k && r >= box.r0 && r <= box.r1 && c >= box.c0 && c <= box.c1) this.dirty.add(gidOf(t.ti, src));
    t.links.delete(k);
    if (!raw) {
      t.cells.delete(k);
      t.formulas.delete(k);
      t.vals.delete(k);
      t.impl.delete(k);
      t.shrinkLater();
    } else {
      const cell: Cell = { raw };
      if (isFormula(raw)) {
        const p = parsed(raw.slice(1));
        cell.ast = p.ast;
        if (p.perr) cell.perr = p.perr;
        if (p.vol) cell.vol = true;
        if (p.dyn) cell.dyn = true;
        t.formulas.add(k);
      } else {
        cell.lit = readInput(raw, this.opts.dmy);
        t.formulas.delete(k);
        t.vals.delete(k);
        t.impl.delete(k);
      }
      t.cells.set(k, cell);
      if (cell.ast) this.readDeps(t, k);
      t.grow(r, c);
    }
    this.dirty.add(gidOf(t.ti, k));
  }

  /** Every formula worked out again at the next recalc (a whole column's look changed). */
  invalidate() {
    this.full = true;
  }

  /** A cell's look changed: what reads it is worked out again, for the look it takes (a sum of dollars). */
  touch(tabId: string, r: number, c: number) {
    const t = this.byId.get(tabId);
    if (t) this.dirty.add(gidOf(t.ti, keyOf(r, c)));
  }

  /** Every cell of a tab at once, from what was typed in each (A1 address or key to the input). */
  loadTab(tabId: string, cells: Iterable<[number, string]>) {
    const t = this.byId.get(tabId);
    if (!t) return;
    const next = new Map(cells);
    for (const k of [...t.cells.keys()]) if (!next.has(k)) this.setCell(tabId, Math.floor(k / 1024), k % 1024, null);
    for (const [k, raw] of next) this.setCell(tabId, Math.floor(k / 1024), k % 1024, raw);
  }

  // ------------------------------------------------------------- what comes out

  tabIds(): string[] {
    return this.tabs.map((t) => t.id);
  }

  /** A cell's value: a formula's worked out, a spilled block's part, or what was typed, read. */
  get(tabId: string, r: number, c: number): Scalar {
    return this.byId.get(tabId)?.get(r, c) ?? null;
  }

  raw(tabId: string, r: number, c: number): string {
    return this.byId.get(tabId)?.cells.get(keyOf(r, c))?.raw ?? "";
  }

  /** How a cell's number was typed or worked out (currency, a date), for its look when no format is chosen. */
  implied(tabId: string, r: number, c: number): Implied | undefined {
    const t = this.byId.get(tabId);
    if (!t) return undefined;
    const k = keyOf(r, c);
    const cell = t.cells.get(k);
    if (cell?.lit) return cell.lit.implied;
    return t.impl.get(k);
  }

  /** The address a formula's HYPERLINK gave a cell, or null. */
  link(tabId: string, r: number, c: number): string | null {
    return this.byId.get(tabId)?.links.get(keyOf(r, c)) ?? null;
  }

  /** Whether a cell shows part of a block another cell's formula spilled. */
  spilled(tabId: string, r: number, c: number): boolean {
    return !!this.byId.get(tabId)?.spill.has(keyOf(r, c));
  }

  /** The cells in use on a tab: its last used row and column, plus one each. */
  extent(tabId: string): { rows: number; cols: number } {
    const t = this.byId.get(tabId);
    if (!t) return { rows: 0, cols: 0 };
    t.fit();
    return { rows: t.rows, cols: t.cols };
  }

  /** The cells holding something, as keys, for a tab. */
  keys(tabId: string): number[] {
    const t = this.byId.get(tabId);
    return t ? [...t.cells.keys()] : [];
  }

  isFormulaAt(tabId: string, r: number, c: number): boolean {
    return !!this.byId.get(tabId)?.formulas.has(keyOf(r, c));
  }

  // ------------------------------------------------------------- working it out

  /** Works out what changed since the last time, and every formula that reads it. */
  recalc() {
    const all = this.full;
    this.full = false;
    const dirty = this.dirty;
    this.dirty = new Set();
    this.budget = new Budget(50_000_000);
    let todo: Set<number>;
    if (all) {
      todo = new Set();
      for (const t of this.tabs) for (const k of t.formulas) todo.add(gidOf(t.ti, k));
    } else {
      todo = this.dependents(dirty);
      for (const t of this.tabs) for (const k of t.formulas) if (t.cells.get(k)?.vol) todo.add(gidOf(t.ti, k));
    }
    // blocks a formula spills change what cells beside it hold: those that read them are worked out again
    for (let pass = 0; pass < 3 && todo.size; pass++) {
      const moved = this.run(todo);
      if (!moved.size) break;
      todo = this.dependents(moved, true);
    }
    // OFFSET and INDIRECT read cells no reference names, so the order could not put them after those: once more, with
    // what reads them
    const dyn = new Set<number>();
    for (const t of this.tabs) for (const k of t.formulas) if (t.cells.get(k)?.dyn) dyn.add(gidOf(t.ti, k));
    if (dyn.size) this.run(this.dependents(dyn));
  }

  /** The formula cells that read any of these cells, at any distance, with the formulas among them. */
  private dependents(changed: Set<number>, onlyReaders = false): Set<number> {
    const out = new Set<number>();
    let frontier: number[] = [];
    for (const g of changed) {
      const { ti, k } = splitGid(g);
      if (!onlyReaders && this.tabs[ti]?.formulas.has(k)) out.add(g);
      frontier.push(g);
    }
    const formulas: { g: number; deps: Dep[] }[] = [];
    for (const t of this.tabs) for (const k of t.formulas) formulas.push({ g: gidOf(t.ti, k), deps: t.cells.get(k)?.deps ?? [] });
    while (frontier.length) {
      // the changed cells of each tab, and the block around them, for a quick no
      const byTab = new Map<number, { cells: { r: number; c: number }[]; box: Rect }>();
      for (const g of frontier) {
        const { ti, k } = splitGid(g);
        const { r, c } = fromKey(k);
        const b = byTab.get(ti);
        if (!b) byTab.set(ti, { cells: [{ r, c }], box: { r0: r, c0: c, r1: r, c1: c } });
        else {
          b.cells.push({ r, c });
          b.box = { r0: Math.min(b.box.r0, r), c0: Math.min(b.box.c0, c), r1: Math.max(b.box.r1, r), c1: Math.max(b.box.c1, c) };
        }
      }
      const next: number[] = [];
      for (const f of formulas) {
        if (out.has(f.g)) continue;
        let hit = false;
        for (const d of f.deps) {
          const b = byTab.get(d.ti);
          if (!b || d.r0 > b.box.r1 || d.r1 < b.box.r0 || d.c0 > b.box.c1 || d.c1 < b.box.c0) continue;
          if (b.cells.some((x) => x.r >= d.r0 && x.r <= d.r1 && x.c >= d.c0 && x.c <= d.c1)) {
            hit = true;
            break;
          }
        }
        if (hit) {
          out.add(f.g);
          next.push(f.g);
        }
      }
      frontier = next;
    }
    return out;
  }

  /** The formulas inside a block that are to be worked out now, for the order. */
  private within(d: Dep, todo: Set<number>, byTab: Map<number, number[]>): number[] {
    const t = this.tabs[d.ti];
    if (!t) return [];
    const r1 = Math.min(d.r1, MAX_ROWS - 1);
    const c1 = Math.min(d.c1, MAX_COLS - 1);
    const area = (r1 - d.r0 + 1) * (c1 - d.c0 + 1);
    const out: number[] = [];
    const list = byTab.get(d.ti) ?? [];
    if (area <= list.length * 4) {
      for (let r = d.r0; r <= r1; r++)
        for (let c = d.c0; c <= c1; c++) {
          const g = gidOf(d.ti, keyOf(r, c));
          if (todo.has(g)) out.push(g);
        }
    } else
      for (const g of list) {
        const { r, c } = fromKey(g % 16_777_216);
        if (r >= d.r0 && r <= d.r1 && c >= d.c0 && c <= d.c1) out.push(g);
      }
    return out;
  }

  /** Works out these formula cells, each after the ones it reads; says which cells their spilled blocks changed. */
  private run(todo: Set<number>): Set<number> {
    const byTab = new Map<number, number[]>();
    for (const g of todo) {
      const ti = Math.floor(g / 16_777_216);
      const l = byTab.get(ti);
      if (l) l.push(g);
      else byTab.set(ti, [g]);
    }
    const state = new Map<number, 1 | 2>();
    const order: number[] = [];
    const loop = new Set<number>();
    for (const start of todo) {
      if (state.has(start)) continue;
      const stack: { g: number; next: number[]; i: number }[] = [];
      const push = (g: number) => {
        state.set(g, 1);
        const { ti, k } = splitGid(g);
        const deps = this.tabs[ti]?.cells.get(k)?.deps ?? [];
        stack.push({ g, next: deps.flatMap((d) => this.within(d, todo, byTab)), i: 0 });
      };
      push(start);
      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.i < top.next.length) {
          const p = top.next[top.i++];
          const st = state.get(p);
          if (st === undefined) push(p);
          else if (st === 1) {
            // a loop: every cell on it, from where it comes round
            for (let j = stack.length - 1; j >= 0; j--) {
              loop.add(stack[j].g);
              if (stack[j].g === p) break;
            }
          }
        } else {
          state.set(top.g, 2);
          order.push(top.g);
          stack.pop();
        }
      }
    }
    const moved = new Set<number>();
    const now = nowSerial(this.opts.now ?? Date.now(), this.opts.tz);
    for (const g of order) {
      const { ti, k } = splitGid(g);
      const t = this.tabs[ti];
      const cell = t?.cells.get(k);
      if (!t || !cell || cell.lit) continue;
      const { r, c } = fromKey(k);
      // a spilled block from before is cleared first, its cells counted as changed
      const before = t.spillOf.get(k);
      if (before) {
        for (let i = before.r0; i <= before.r1; i++)
          for (let j = before.c0; j <= before.c1; j++) {
            if (i === r && j === c) continue;
            const sk = keyOf(i, j);
            if (t.spill.delete(sk)) {
              moved.add(gidOf(ti, sk));
              if (!t.cells.has(sk)) t.impl.delete(sk);
            }
          }
        t.spillOf.delete(k);
        t.shrinkLater();
      }
      let v: Scalar;
      this.linked = null;
      if (loop.has(g)) v = err("#CYCLE!", "This formula refers back to itself.");
      else if (!cell.ast) v = err("#ERROR!", cell.perr ?? "The formula cannot be read.");
      else {
        let out: Val;
        try {
          out = this.evalNode(cell.ast, t, r, c, now, 0);
        } catch (e) {
          out = e instanceof SheetErr ? e : err("#ERROR!", "The formula could not be worked out.");
        }
        v = this.place(t, r, c, out, moved);
        // a HYPERLINK in the formula's answer makes the cell a link to its address
        if (this.linked && !isBlock(out) && !isErr(v) && v !== null) t.links.set(k, this.linked);
        else t.links.delete(k);
      }
      t.vals.set(k, v);
      const imp = typeof v === "number" && cell.ast ? (this.blockLook ?? this.impliedOf(cell.ast, t, 0)) : undefined;
      this.blockLook = undefined;
      if (imp) t.impl.set(k, imp);
      else t.impl.delete(k);
    }
    return moved;
  }

  /** A formula's result in its cell: a block spills to the right and down when the cells there are free. */
  private place(t: Tab, r: number, c: number, out: Val, moved: Set<number>): Scalar {
    if (!isBlock(out)) return out;
    const h = out.h;
    const w = out.w;
    if (!h || !w) return null;
    if (h === 1 && w === 1) return out.at(0, 0);
    if (h * w > 200_000) return err("#VALUE!", "The result is too large to show.");
    if (r + h > MAX_ROWS || c + w > MAX_COLS) return err("#REF!", "The result would run off the sheet.");
    const src = keyOf(r, c);
    t.blocked.delete(src);
    for (let i = 0; i < h; i++)
      for (let j = 0; j < w; j++) {
        if (!i && !j) continue;
        const k = keyOf(r + i, c + j);
        if (t.cells.has(k) || t.spill.has(k)) {
          t.blocked.set(src, { r0: r, c0: c, r1: r + h - 1, c1: c + w - 1 });
          return err("#REF!", `The result was not spilled because ${colName(c + j)}${r + i + 1} is not empty.`);
        }
      }
    // each spilled cell takes the look of the cell it came from: a block of cells read whole, or whole rows of one
    const lookOf = (i: number, j: number): Implied | undefined => {
      if (out instanceof RangeV) return this.implAt(out.tab as Tab, out.r0 + i, out.c0 + j);
      if (out.from) {
        const src = out.from.range;
        const sr = out.from.rows[i];
        return sr === undefined || j >= src.w ? undefined : this.implAt(src.tab as Tab, src.r0 + sr, src.c0 + j);
      }
      return undefined;
    };
    for (let i = 0; i < h; i++)
      for (let j = 0; j < w; j++) {
        if (!i && !j) continue;
        const k = keyOf(r + i, c + j);
        t.spill.set(k, out.at(i, j));
        const look = typeof out.at(i, j) === "number" ? lookOf(i, j) : undefined;
        if (look) t.impl.set(k, look);
        else t.impl.delete(k);
        moved.add(gidOf(t.ti, k));
        t.grow(r + i, c + j);
      }
    t.spillOf.set(keyOf(r, c), { r0: r, c0: c, r1: r + h - 1, c1: c + w - 1 });
    this.blockLook = typeof out.at(0, 0) === "number" ? lookOf(0, 0) : undefined;
    return out.at(0, 0);
  }

  private readDeps(t: Tab, k: number) {
    const cell = t.cells.get(k);
    if (!cell?.ast) return;
    const deps: Dep[] = [];
    const walk = (n: Ast) => {
      if (n.t === "ref") {
        const d = this.depOf(n.ref, t);
        if (d) deps.push(d);
      } else if (n.t === "name") {
        // a named range reads its cells, wherever they are
        const nr = this.names.get(n.name.toUpperCase());
        const tab = nr?.box ? this.byId.get(nr.tabId) : null;
        if (tab && nr?.box) deps.push({ ti: tab.ti, ...nr.box });
      } else if (n.t === "fn") n.args.forEach((a) => a && walk(a));
      else if (n.t === "bin") {
        walk(n.a);
        walk(n.b);
      } else if (n.t === "un" || n.t === "pct") walk(n.a);
      else if (n.t === "arr") n.rows.forEach((r) => r.forEach(walk));
    };
    walk(cell.ast);
    cell.deps = deps;
  }

  private tabNamed(name: string | null, here: Tab): Tab | null {
    if (name === null) return here;
    const n = name.toLowerCase();
    return this.tabs.find((t) => t.name.toLowerCase() === n) ?? null;
  }

  private depOf(ref: Ref, here: Tab): Dep | null {
    const t = this.tabNamed(ref.sheet, here);
    if (!t || ref.dead) return null;
    const box = refBox(ref);
    return box ? { ti: t.ti, ...box } : null;
  }

  // ------------------------------------------------------------- one formula

  private evalNode(n: Ast, t: Tab, r: number, c: number, now: number, depth: number, scope?: Map<string, Val>): Val {
    if (depth > 120) throw err("#ERROR!", "That formula is nested too deep.");
    switch (n.t) {
      case "num":
        return n.v;
      case "str":
        return n.v;
      case "bool":
        return n.v;
      case "err":
        return err(n.v);
      case "name": {
        // a name LET gave a value, then a named range (Fixes 9), else no name this sheet knows
        const bound = scope?.get(n.name.toUpperCase());
        if (bound !== undefined) return bound;
        const nr = this.names.get(n.name.toUpperCase());
        if (nr) {
          const tab = this.byId.get(nr.tabId);
          if (!tab || !nr.box) return err("#REF!", `The cells named ${nr.name} were deleted.`);
          return new RangeV(tab, nr.box.r0, nr.box.c0, Math.min(nr.box.r1, MAX_ROWS - 1), Math.min(nr.box.c1, MAX_COLS - 1));
        }
        return err("#NAME?", `Unknown name: ${n.name}.`);
      }
      case "ref": {
        const tab = this.tabNamed(n.ref.sheet, t);
        if (!tab) return err("#REF!", `There is no tab called "${n.ref.sheet}".`);
        if (n.ref.dead) return err("#REF!", "This formula refers to cells that were deleted.");
        const box = refBox(n.ref);
        if (!box || box.r0 < 0 || box.c0 < 0 || box.r0 >= MAX_ROWS || box.c0 >= MAX_COLS) return err("#REF!", "That reference is outside the sheet.");
        return new RangeV(tab, box.r0, box.c0, Math.min(box.r1, MAX_ROWS - 1), Math.min(box.c1, MAX_COLS - 1));
      }
      case "arr":
        return new Arr(n.rows.map((row) => row.map((x) => scalar(this.evalNode(x, t, r, c, now, depth + 1, scope)))));
      case "un": {
        const a = this.evalNode(n.a, t, r, c, now, depth + 1, scope);
        return this.map1(a, (x) => {
          const v = toNum(x, this.opts.dmy);
          return isErr(v) ? v : n.op === "-" ? -v : v;
        });
      }
      case "pct": {
        const a = this.evalNode(n.a, t, r, c, now, depth + 1, scope);
        return this.map1(a, (x) => {
          const v = toNum(x, this.opts.dmy);
          return isErr(v) ? v : v / 100;
        });
      }
      case "bin": {
        const a = this.evalNode(n.a, t, r, c, now, depth + 1, scope);
        const b = this.evalNode(n.b, t, r, c, now, depth + 1, scope);
        return this.map2(a, b, (x, y) => this.binary(n.op, x, y));
      }
      case "fn": {
        if (n.name === "LET") return this.let(n.args, t, r, c, now, depth, scope);
        const spec = FUNCS[n.name];
        if (!spec) return err("#NAME?", MISSING[n.name] ?? `Unknown function: ${n.name}.`);
        if (n.args.length < spec.min || n.args.length > spec.max) return err("#N/A", `${n.name} takes ${spec.min === spec.max ? spec.min : `${spec.min} to ${spec.max}`} argument${spec.max === 1 ? "" : "s"}: ${spec.sig}.`);
        const memo = new Map<number, Val>();
        const dmy = !!this.opts.dmy;
        const val = (i: number): Val => {
          if (memo.has(i)) return memo.get(i)!;
          const node = n.args[i];
          const v = node ? this.evalNode(node, t, r, c, now, depth + 1, scope) : null;
          memo.set(i, v);
          return v;
        };
        const sc = (i: number): Scalar => scalar(val(i));
        const args: Args = {
          n: n.args.length,
          has: (i) => i < n.args.length && n.args[i] !== null,
          val,
          sc,
          num: (i, dflt) => {
            if (!args.has(i)) {
              if (dflt === undefined) return 0;
              return dflt;
            }
            const v = toNum(sc(i), dmy);
            if (isErr(v)) throw v;
            return v;
          },
          str: (i, dflt) => {
            if (!args.has(i)) return dflt ?? "";
            const v = toStr(sc(i));
            if (isErr(v)) throw v;
            return v;
          },
          bool: (i, dflt) => {
            if (!args.has(i)) return dflt ?? false;
            const v = toBool(sc(i));
            if (isErr(v)) throw v;
            return v;
          },
          dmy,
          now,
          budget: this.budget,
          here: { tab: t, r, c },
          setLink: (url) => {
            this.linked = url;
          },
          ref: (text) => this.refFrom(text, t, r, c, now, depth),
        };
        try {
          return spec.run(args);
        } catch (e) {
          if (e instanceof SheetErr) return e;
          throw e;
        }
      }
    }
  }

  /** LET(name, value, [name2, value2, …], formula): each value worked out once, by its name, for the formula. */
  private let(args: (Ast | null)[], t: Tab, r: number, c: number, now: number, depth: number, scope?: Map<string, Val>): Val {
    if (args.length < 3 || args.length % 2 === 0) return err("#N/A", "LET takes names and values in pairs, then a formula: LET(x, 5, x*2).");
    const inner = new Map(scope ?? []);
    for (let i = 0; i + 1 < args.length; i += 2) {
      const name = args[i];
      if (!name || name.t !== "name") return err("#ERROR!", "Each name in LET is a word, such as total or price, not a cell or a value.");
      const value = args[i + 1];
      inner.set(name.name.toUpperCase(), value ? this.evalNode(value, t, r, c, now, depth + 1, inner) : null);
    }
    const body = args[args.length - 1];
    return body ? this.evalNode(body, t, r, c, now, depth + 1, inner) : null;
  }

  /** A reference written as text (INDIRECT), as the cells it names: "B2", "A1:C9", "'Tab name'!A1". */
  private refFrom(text: string, t: Tab, r: number, c: number, now: number, depth: number): Val {
    let ast: Ast;
    try {
      ast = parse(text.trim());
    } catch {
      return err("#REF!", `"${text.slice(0, 40)}" is not a reference.`);
    }
    // a reference, or a named range's name
    if (ast.t !== "ref" && !(ast.t === "name" && this.names.has(ast.name.toUpperCase()))) return err("#REF!", `"${text.slice(0, 40)}" is not a reference.`);
    return this.evalNode(ast, t, r, c, now, depth + 1);
  }

  private map1(a: Val, f: (x: Scalar) => Scalar): Val {
    if (!isBlock(a)) return f(a);
    this.budget.spend(a.h * a.w);
    return new Arr(Array.from({ length: a.h }, (_, i) => Array.from({ length: a.w }, (_, j) => f(a.at(i, j)))));
  }

  /** Two values with an operator: two blocks cell by cell, a block and a value with the value for each cell. */
  private map2(a: Val, b: Val, f: (x: Scalar, y: Scalar) => Scalar): Val {
    const A = isBlock(a);
    const B = isBlock(b);
    if (!A && !B) return f(a as Scalar, b as Scalar);
    // a single cell is a value
    if (A && a.h === 1 && a.w === 1 && !B) return f(a.at(0, 0), b as Scalar);
    if (B && b.h === 1 && b.w === 1 && !A) return f(a as Scalar, b.at(0, 0));
    if (A && B && a.h === 1 && a.w === 1 && b.h === 1 && b.w === 1) return f(a.at(0, 0), b.at(0, 0));
    const h = Math.max(A ? a.h : 1, B ? b.h : 1);
    const w = Math.max(A ? a.w : 1, B ? b.w : 1);
    this.budget.spend(h * w);
    const pick = (v: Val, i: number, j: number): Scalar => {
      if (!isBlock(v)) return v;
      const ii = v.h === 1 ? 0 : i;
      const jj = v.w === 1 ? 0 : j;
      return ii < v.h && jj < v.w ? v.at(ii, jj) : err("#N/A", "The ranges are not the same size.");
    };
    return new Arr(Array.from({ length: h }, (_, i) => Array.from({ length: w }, (_, j) => f(pick(a, i, j), pick(b, i, j)))));
  }

  private binary(op: string, x: Scalar, y: Scalar): Scalar {
    if (isErr(x)) return x;
    if (isErr(y)) return y;
    const dmy = this.opts.dmy;
    switch (op) {
      case "&": {
        const a = toStr(x);
        const b = toStr(y);
        return isErr(a) ? a : isErr(b) ? b : a + b;
      }
      case "=":
        return cmp(x, y) === 0;
      case "<>":
        return cmp(x, y) !== 0;
      case "<":
        return cmp(x, y) < 0;
      case ">":
        return cmp(x, y) > 0;
      case "<=":
        return cmp(x, y) <= 0;
      case ">=":
        return cmp(x, y) >= 0;
    }
    const a = toNum(x, dmy);
    if (isErr(a)) return a;
    const b = toNum(y, dmy);
    if (isErr(b)) return b;
    let v: number;
    switch (op) {
      case "+":
        v = a + b;
        break;
      case "-":
        v = a - b;
        break;
      case "*":
        v = a * b;
        break;
      case "/":
        if (b === 0) return err("#DIV/0!", "This formula divides by zero.");
        v = a / b;
        break;
      case "^":
        if (a === 0 && b < 0) return err("#DIV/0!", "Zero to a negative power divides by zero.");
        v = Math.pow(a, b);
        break;
      default:
        return err("#ERROR!", `Unknown operator ${op}.`);
    }
    return Number.isFinite(v) ? v : err("#NUM!", "The result is too large.");
  }

  // ------------------------------------------------------------- how a result should look

  private implAt(t: Tab, r: number, c: number): Implied | undefined {
    const f = this.opts.fmtAt?.(t.id, r, c);
    if (f?.nf && f.nf !== "auto" && f.nf !== "text") return { nf: f.nf, dp: f.dp, sym: f.sym };
    const k = keyOf(r, c);
    const cell = t.cells.get(k);
    return cell?.lit ? cell.lit.implied : t.impl.get(k);
  }

  /** The look of the first number in a block. */
  private implIn(tab: Tab, box: Rect): Implied | undefined {
    const r1 = Math.min(box.r1, Math.max(tab.rows - 1, box.r0));
    const c1 = Math.min(box.c1, Math.max(tab.cols - 1, box.c0));
    let seen = 0;
    for (let r = box.r0; r <= r1; r++)
      for (let c = box.c0; c <= c1; c++) {
        if (++seen > 400) return undefined;
        const v = tab.get(r, c);
        if (typeof v === "number") return this.implAt(tab, r, c);
      }
    return undefined;
  }

  /** The look a formula's number takes from what it reads: a sum of dollars is in dollars, TODAY() is a date. */
  private impliedOf(n: Ast, t: Tab, depth: number): Implied | undefined {
    if (depth > 30) return undefined;
    const sub = (x: Ast | null | undefined) => (x ? this.impliedOf(x, t, depth + 1) : undefined);
    switch (n.t) {
      case "name":
      case "ref": {
        const nr = n.t === "name" ? this.names.get(n.name.toUpperCase()) : undefined;
        const tab = n.t === "ref" ? this.tabNamed(n.ref.sheet, t) : nr ? (this.byId.get(nr.tabId) ?? null) : null;
        const box = n.t === "ref" ? refBox(n.ref) : (nr?.box ?? null);
        if (!tab || !box) return undefined;
        return this.implIn(tab, box);
      }
      case "un":
        return sub(n.a);
      case "bin": {
        const a = sub(n.a);
        const b = sub(n.b);
        const dateish = (x?: Implied) => x?.nf === "date" || x?.nf === "datetime";
        if (n.op === "+" || n.op === "-") {
          if (dateish(a) && dateish(b)) return n.op === "-" ? undefined : a;
          if (dateish(a)) return a;
          if (dateish(b) && n.op === "+") return b;
          return a ?? b;
        }
        if (n.op === "*") return a?.nf === "cur" ? a : b?.nf === "cur" ? b : undefined;
        if (n.op === "/") return a?.nf === "cur" && b?.nf !== "cur" ? a : undefined;
        return undefined;
      }
      case "fn":
        switch (n.name) {
          case "TODAY":
          case "DATE":
          case "EDATE":
          case "EOMONTH":
          case "WORKDAY":
          case "DATEVALUE":
            return { nf: "date" };
          case "LARGE":
          case "SMALL":
          case "PERCENTILE":
          case "PERCENTILE.INC":
            return sub(n.args[0]);
          case "NOW":
            return { nf: "datetime" };
          case "TIME":
            return { nf: "time" };
          case "SUM":
          case "AVERAGE":
          case "MIN":
          case "MAX":
          case "MEDIAN":
          case "ROUND":
          case "ROUNDUP":
          case "ROUNDDOWN":
          case "ABS":
          case "INT":
          case "CEILING":
          case "FLOOR":
          case "SUMIFS":
          case "AVERAGEIFS":
          case "MINIFS":
          case "MAXIFS":
            return sub(n.args[0]);
          case "SUMIF":
          case "AVERAGEIF":
            return sub(n.args[2] ?? n.args[0]);
          case "IF":
            return sub(n.args[1]) ?? sub(n.args[2]);
          case "IFERROR":
          case "IFNA":
            return sub(n.args[0]);
          case "INDEX":
          case "VLOOKUP":
          case "HLOOKUP": {
            // the look of the column (an HLOOKUP's row) the answer comes from, when the call names it by number, never
            // the first number in the block: an amount beside a date read as a date in 1901 (Fixes 10)
            const src = n.args[n.name === "INDEX" ? 0 : 1];
            const num = (x: Ast | null | undefined) => (x?.t === "num" && x.v >= 1 ? Math.floor(x.v) : 0);
            const col = n.name === "HLOOKUP" ? 0 : num(n.args[2]);
            const row = n.name === "HLOOKUP" ? num(n.args[2]) : n.name === "INDEX" ? num(n.args[1]) : 0;
            const tab = src?.t === "ref" ? this.tabNamed(src.ref.sheet, t) : null;
            const box = src?.t === "ref" ? refBox(src.ref) : null;
            if (tab && box && (col || row)) return this.implIn(tab, { r0: row ? box.r0 + row - 1 : box.r0, r1: row ? box.r0 + row - 1 : box.r1, c0: col ? box.c0 + col - 1 : box.c0, c1: col ? box.c0 + col - 1 : box.c1 });
            return sub(src);
          }
          case "XLOOKUP":
            return sub(n.args[2]);
        }
        return undefined;
      default:
        return undefined;
    }
  }
}

/** The block a reference names; a whole column runs to the foot of the sheet, a whole row across it. */
export function refBox(ref: Ref): Rect | null {
  const { a, b } = ref;
  if (!b) return a.c === null || a.r === null ? null : { r0: a.r, c0: a.c, r1: a.r, c1: a.c };
  if (a.r === null && b.r === null && a.c !== null && b.c !== null) return { r0: 0, c0: Math.min(a.c, b.c), r1: MAX_ROWS - 1, c1: Math.max(a.c, b.c) };
  if (a.c === null && b.c === null && a.r !== null && b.r !== null) return { r0: Math.min(a.r, b.r), c0: 0, r1: Math.max(a.r, b.r), c1: MAX_COLS - 1 };
  if (a.c !== null && a.r !== null && b.c !== null && b.r === null) return { r0: a.r, c0: Math.min(a.c, b.c), r1: MAX_ROWS - 1, c1: Math.max(a.c, b.c) };
  if (a.c !== null && a.r !== null && b.c !== null && b.r !== null) return { r0: Math.min(a.r, b.r), c0: Math.min(a.c, b.c), r1: Math.max(a.r, b.r), c1: Math.max(a.c, b.c) };
  return null;
}

/** The error a value is, with its words, for a cell's hover and an agent's read. */
export function errorWords(v: Scalar): string | null {
  return isErr(v) ? v.msg || v.code : null;
}
